import React from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
} from '@material-ui/core'
import { useFieldArray, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import * as yup from 'yup'
import {
  CtrlTextField,
  CtrlCheckbox,
  EmailFieldWithSuggestions,
} from 'components/form-util'
import { useMutation } from '@apollo/client'
import { INVITE_GUARDIAN } from '../queries'
import { useSnackbar, useSnackbarError, useSnackbarSuccess } from 'lib/snackbar'

const validationSchema = yup.object().shape({
  firstName: yup
    .string()
    .min(1)
    .label('First Name')
    .required('First name is required'),
  lastName: yup
    .string()
    .min(1)
    .label('Last Name')
    .required('Last name is required'),
  email: yup
    .string()
    .email('Invalid email address')
    .required('Email is required'),
  students: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.string().required(),
        firstName: yup.string(),
        lastName: yup.string(),
        checked: yup.boolean(),
      }),
    )
    .test(
      'at-least-one-checked',
      'At least one student must be selected',
      value =>
        Array.isArray(value) && value.some(student => student.checked === true),
    ),
})

const InviteGuardianModal = ({ open, onClose, students, refetch }) => {
  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      students: students,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  })

  const { enqueueSnackbar } = useSnackbar()
  const snackbarError = useSnackbarError(enqueueSnackbar)
  const snackbarSuccess = useSnackbarSuccess(enqueueSnackbar)

  const {
    handleSubmit,
    control,
    errors,
    reset,
    formState: { isSubmitting, isValid },
  } = form

  const { fields } = useFieldArray({
    control,
    name: 'students',
  })

  const [inviteGuardian] = useMutation(INVITE_GUARDIAN)

  const handleFormSubmit = async formValues => {
    const selectedStudentIds = formValues.students
      .filter(student => student.checked)
      .map(student => student.id)

    try {
      const response = await inviteGuardian({
        variables: {
          input: {
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            email: formValues.email,
            studentIds: selectedStudentIds,
          },
        },
      })
      if (response?.data?.inviteJrlGuardian?.success) {
        snackbarSuccess('Guardian invitation sent successfully')
        reset()
        onClose()
      } else {
        snackbarError('Failed to send invitation')
      }
    } catch (e) {
      snackbarError(e?.message || 'Failed to send invitation')
    }
    refetch()
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="invite-guardian-dialog-title"
    >
      <DialogTitle id="invite-guardian-dialog-title">
        Invite Guardian
      </DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          <DialogContentText>
            Invite an additional guardian to help manage junior players. A
            guardian can fully manage junior player accounts, participate in
            team chats, and manage schedules/RSVPs.
          </DialogContentText>
          {fields.map((student, i) => {
            return (
              <Box key={student.id} mb={1}>
                <CtrlCheckbox
                  name={`students[${i}].checked`}
                  label={`${student.firstName} ${student.lastName}`}
                  control={control}
                  errors={errors}
                />
                <input
                  type="hidden"
                  name={`students[${i}].id`}
                  value={student.id}
                  ref={control.register()}
                />
              </Box>
            )
          })}
          <Box mb={2} mt={2}>
            <CtrlTextField
              name="firstName"
              label="First Name"
              required
              {...{ control, errors }}
            />
          </Box>
          <Box mb={2}>
            <CtrlTextField
              name="lastName"
              label="Last Name"
              required
              {...{ control, errors }}
            />
          </Box>
          <Box mb={2}>
            <EmailFieldWithSuggestions
              name="email"
              label="Email *"
              variant="outlined"
              control={control}
              errors={errors}
              classes={{}}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={isSubmitting || !isValid}
          >
            Invite
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default InviteGuardianModal
